import { isRxDocument } from "rxdb"
import { i18n } from "src/boot/i18n"
import { ItemDocument } from "src/db/collectionDefinitions/csi/item"
import { getDB } from "src/db/databases"
import router from "src/router"
import { getAttributeValueIDFromEntity, useCacheStore } from "src/stores/cacheStore"
import { Log } from "src/util/classes/log"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { NamedManagerPath } from "src/util/enums/namedPaths"
import { attributeSelectToString, attributeTemporalToString } from "src/util/functions/attributes"
import { getIssuePriorityColor } from "src/util/functions/issuePriority"
import { getIssuesOnItemForItem } from "src/util/functions/issues"
import { getItemClassData } from "src/util/functions/itemClass"
import { getItemModelData } from "src/util/functions/itemModel"
import { getWorkOrderPriorityColor } from "src/util/functions/workOrderPriority"
import { getItem } from "src/util/http/csi/items"
import { IconTableRowElement, RowData, TableColumn } from "src/util/types/components/sfxTable"
import {
  AttributeValueCharFull,
  AttributeValueNumericFull,
  AttributeValueSelectFull,
  AttributeValueTemporalFull,
} from "src/util/types/dataModels/attribute"
import { IssuePriority } from "src/util/types/dataModels/issuePriority"
import { Item, ItemCardData, ItemTableData } from "src/util/types/dataModels/item"
import { WorkOrderRegularItem } from "src/util/types/dataModels/workOrder"
import { WorkOrderPriority } from "src/util/types/dataModels/workOrderPriority"
import { IdentifierType } from "src/util/types/dataModels/identifier"
import { getPerformedCorrectiveWorkForItem, getPerformedRegularWorkForItem } from "./performedWork"

export async function getItemData(itemId: string): Promise<Item | undefined> {
  try {
    const cacheStore = useCacheStore()
    const itemInCache = cacheStore.item.get(itemId)

    if (itemInCache) {
      return itemInCache
    } else {
      const db = await getDB()
      const itemInDb = await db.item.findOne(itemId).exec()

      if (itemInDb) {
        const mutableItem = itemInDb.toMutableJSON()
        cacheStore.item.set(mutableItem.id, mutableItem)
        return mutableItem
      } else {
        return await getItem({}, itemId)
      }
    }
  } catch (error) {
    Log.error({ message: "An error occurred while fetching Item data", error })
  }
}

export async function getItemDataByIds(itemIds: string[]): Promise<Item[]> {
  const cacheStore = useCacheStore()
  let missingIds: string[] = []
  let foundItems: Item[] = []

  for (const itemId of itemIds) {
    const cachedItem = cacheStore.item.get(itemId)
    if (cachedItem) {
      foundItems.push(cachedItem)
    } else {
      missingIds.push(itemId)
    }
  }

  if (missingIds.length > 0) {
    const db = await getDB()
    const itemsInDbMap = await db.item.findByIds(missingIds).exec()
    const itemsInDb = Array.from(itemsInDbMap.values()).map((value) => value.toMutableJSON())

    foundItems = foundItems.concat(itemsInDb)

    if (itemsInDb.length !== missingIds.length) {
      missingIds = missingIds.filter((id) => !itemsInDbMap.has(id))

      // We need to ensure that this function is called sparsely and item data is always up-to-date
      // Else we need to bother doing individual fetch requests for each missing ID
      Log.error({ message: "Missing item IDs found", data: { ids: missingIds } })
    }
  }

  return foundItems
}

export async function getItemCardBaseData(item: Item): Promise<ItemCardData> {
  const cacheStore = useCacheStore()

  return {
    itemData: cacheStore.item.get(item.id) || item,
    issuePriorityData: await getIssuePriorityDataListForItem(item.id),
    workOrderPriorityData: getWorkOrderPriorityDataListForItem(item.id),
  }
}

/**
 * Sort the items on:
 * 1. Work Order priority (not implemented)
 * 2. Issue priority
 * 3. Change Requests (not implemented)
 * 4. Name
 *
 * This is applicable for both card and table data.
 *
 * @param a
 * @param b
 */
export function sortItemData(
  a: ItemCardData | ItemTableData,
  b: ItemCardData | ItemTableData
): number {
  // Order by rank or risk or designation, depending on the available data.
  const rankOrder =
    (b.workOrderPriorityData ? b.workOrderPriorityData[0].rank : 0) -
    (a.workOrderPriorityData ? a.workOrderPriorityData[0].rank : 0)
  const riskOrder =
    (b.issuePriorityData ? b.issuePriorityData[0].risk_factor : 0) -
    (a.issuePriorityData ? a.issuePriorityData[0].risk_factor : 0)
  const designationOrder = a.itemData.designation.localeCompare(b.itemData.designation)

  return rankOrder || riskOrder || designationOrder
}

/**
 * @deprecated Use the cacheStore directly instead
 * @param itemId
 */
export async function getItemWorkOrderListData(
  itemId: string
): Promise<WorkOrderRegularItem[] | undefined> {
  try {
    const cacheStore = useCacheStore()
    const itemWorkOrdersInCache = cacheStore.itemWorkOrders.get(itemId)

    if (itemWorkOrdersInCache) {
      return itemWorkOrdersInCache
    } else {
      const db = await getDB()
      const itemWorkOrdersInDB = await db.item_work_orders.findOne(itemId).exec()

      if (itemWorkOrdersInDB) {
        const itemWorkOrderMutable = itemWorkOrdersInDB.toMutableJSON()
        cacheStore.itemWorkOrders.set(
          itemWorkOrderMutable.item_id,
          itemWorkOrderMutable.work_orders
        )
        return itemWorkOrderMutable.work_orders
      }

      const { getWorkOrderListForItem } = await import("src/util/http/csi/items")
      const itemWorkOrdersInServer = await getWorkOrderListForItem({}, itemId)

      if (itemWorkOrdersInServer.results.length > 0) {
        cacheStore.itemWorkOrders.set(itemId, itemWorkOrdersInServer.results)
        return itemWorkOrdersInServer.results
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching Work Order list for itemID",
      data: { itemId: itemId },
      error,
    })
  }
}

export async function getIssuePriorityDataListForItem(
  itemId: string
): Promise<IssuePriority[] | undefined> {
  const db = await getDB()

  const issues = await db.issues_on_item.find().where("actionable_item").eq(itemId).exec()
  const priorityIds = issues.map((issue) => issue.priority)

  const prioritiesMap = await db.issue_priority.findByIds(priorityIds).exec()
  if (prioritiesMap.size > 0) {
    const priorities = Array.from(prioritiesMap.values()).map((value) => value.toMutableJSON())
    // Highest priority should be the first element
    return priorities.sort((a, b) => b.risk_factor - a.risk_factor)
  }
}

export function getWorkOrderPriorityDataListForItem(
  itemId: string
): WorkOrderPriority[] | undefined {
  const cacheStore = useCacheStore()
  const cachedWorkOrders = cacheStore.itemWorkOrders.get(itemId)
  if (!cachedWorkOrders) return

  const workOrders = Array.from(cachedWorkOrders.values())
  const priorityIds = workOrders.map((workOrder) => workOrder.priority)
  const uniquePriorityIds = new Set(priorityIds)
  const priorities: WorkOrderPriority[] = []

  for (const id of uniquePriorityIds) {
    const priority = cacheStore.work_order_priority.get(id)
    if (priority) priorities.push(priority)
  }
  if (priorities.length === 0) return

  // Highest priority should be the first element
  return priorities.sort((a, b) => b.rank - a.rank)
}

//region Item Table

export async function toItemTableData(
  itemRaw: Item | ItemDocument
): Promise<RowData<ItemTableData>> {
  const cacheStore = useCacheStore()
  const item = isRxDocument(itemRaw) ? (itemRaw as ItemDocument).toMutableJSON() : itemRaw

  const itemClassData = await getItemClassData(item.item_class)

  const data: RowData<ItemTableData> = {
    itemData: item,
    itemClassData,
    issuePriorityData: await getIssuePriorityDataListForItem(item.id),
    // TODO: Currently this priority list only contains regular work order data.
    // TODO: We need to add corrective work order data as well
    workOrderPriorityData: getWorkOrderPriorityDataListForItem(item.id),
    itemModelData: await getItemModelData(item.item_model || null),
    regularWork: await getPerformedRegularWorkForItem(item.id),
    correctiveWork: await getPerformedCorrectiveWorkForItem(item.id),
    // TODO: regularWorkOrder & correctiveWorkOrder data fetching needs to be updated when we have the offline mode
    regularWorkOrder: cacheStore.itemWorkOrders.get(item.id),
    issueData: await getIssuesOnItemForItem(item.id),
    hasIdentifier: await getItemTableIdentifiers(item.id, item.location || null),
    hasQrIdentifier:
      (await itemHasIdentifierType(item.id, "qr")) ||
      (await locationHasIdentifierType(item.location, "qr")),
    hasRfidIdentifier:
      (await itemHasIdentifierType(item.id, "rfid")) ||
      (await locationHasIdentifierType(item.location, "rfid")),
    attributeChar: getItemTableAttributeCharValues(
      item.id,
      itemClassData?.attributes_char_item || []
    ),
    attributeNumeric: getItemTableAttributeNumericValues(
      item.id,
      itemClassData?.attributes_numeric_item || []
    ),
    attributeSelect: getItemTableAttributeSelectValues(
      item.id,
      itemClassData?.attributes_select_item || []
    ),
    attributeTemporal: getItemTableAttributeTemporalValues(
      item.id,
      itemClassData?.attributes_temporal_item || []
    ),
    link: router.resolve({ name: NamedManagerPath.ITEM, params: { itemId: item.id } }).href,
  }

  if (item.main_image) {
    data.rowImage = { uuid: item.main_image }
  }

  data.status = addItemTableStatusData(data)

  return data
}

//TODO: Identifiers needs to come from cache store when the following Issue is completed
//https://gitlab.com/safeex/safeex-cloud-team/cloud-service-instance-g4/-/issues/48
async function getItemTableIdentifiers(
  itemId: string,
  locationId: string | null
): Promise<boolean> {
  const db = await getDB()
  const hasItemIdentifier = await db.identifier.checkForIdentifier(itemId, "item")

  if (hasItemIdentifier) return true

  if (locationId) {
    return await db.identifier.checkForIdentifier(locationId, "location")
  }

  return false
}

async function itemHasIdentifierType(
  itemId: string, // Item which the identifier belongs to
  identifierType: IdentifierType // "qr" || "rfid" || "barcode"
): Promise<boolean> {
  const db = await getDB()
  return await db.identifier.itemHasIdentifierType(itemId, identifierType)
}

async function locationHasIdentifierType(
  locationId: string | undefined | null, // Location which the identifier belongs to
  identifierType: IdentifierType // "qr" || "rfid" || "barcode"
): Promise<boolean> {
  if (!locationId) return false

  const db = await getDB()
  return await db.identifier.locationHasIdentifierType(locationId, identifierType)
}

function getItemTableAttributeCharValues(
  itemId: string,
  attributeIDs: string[]
): Map<string, AttributeValueCharFull> {
  const attributeMap = new Map<string, AttributeValueCharFull>()
  const cacheStore = useCacheStore()

  for (const attributeID of attributeIDs) {
    const id = getAttributeValueIDFromEntity(itemId, attributeID)
    const value = cacheStore.attribute_char_value.get(id)
    if (value) attributeMap.set(attributeID, value)
  }

  return attributeMap
}

function getItemTableAttributeNumericValues(
  itemId: string,
  attributeIDs: string[]
): Map<string, AttributeValueNumericFull> {
  const attributeMap = new Map<string, AttributeValueNumericFull>()
  const cacheStore = useCacheStore()

  for (const attributeID of attributeIDs) {
    const id = getAttributeValueIDFromEntity(itemId, attributeID)
    const value = cacheStore.attribute_numeric_value.get(id)
    if (value) attributeMap.set(attributeID, value)
  }

  return attributeMap
}

function getItemTableAttributeSelectValues(
  itemId: string,
  attributeIDs: string[]
): Map<string, AttributeValueSelectFull> {
  const attributeMap = new Map<string, AttributeValueSelectFull>()
  const cacheStore = useCacheStore()

  for (const attributeID of attributeIDs) {
    const id = getAttributeValueIDFromEntity(itemId, attributeID)
    const value = cacheStore.attribute_select_value.get(id)
    if (value) attributeMap.set(attributeID, value)
  }

  return attributeMap
}

function getItemTableAttributeTemporalValues(
  itemId: string,
  attributeIDs: string[]
): Map<string, AttributeValueTemporalFull> {
  const attributeMap = new Map<string, AttributeValueTemporalFull>()
  const cacheStore = useCacheStore()

  for (const attributeID of attributeIDs) {
    const id = getAttributeValueIDFromEntity(itemId, attributeID)
    const value = cacheStore.attribute_temporal_value.get(id)
    if (value) attributeMap.set(attributeID, value)
  }

  return attributeMap
}

function addItemTableStatusData(item: ItemTableData): IconTableRowElement[] {
  const issueIcon: IconTableRowElement = {
    column: "issue",
    sortData: item.issuePriorityData ? item.issuePriorityData[0].risk_factor : null,
    name: Icons.ISSUE,
    color: Boolean(item.issuePriorityData)
      ? getIssuePriorityColor(item.issuePriorityData![0])
      : undefined,
  }

  const workOrderIcon: IconTableRowElement = {
    column: "work-order",
    sortData: item.workOrderPriorityData ? item.workOrderPriorityData[0].rank : null,
    name: Icons.WORK_ORDERS,
    color: Boolean(item.workOrderPriorityData)
      ? getWorkOrderPriorityColor(item.workOrderPriorityData![0])
      : undefined,
  }

  return [issueIcon, workOrderIcon]
}

export async function getItemTableAttributesColumns(): Promise<TableColumn[]> {
  let cols: Array<TableColumn> = []

  cols = await addItemTableCharColumns(cols)
  cols = await addItemTableNumericColumns(cols)
  cols = await addItemTableSelectColumns(cols)
  cols = await addItemTableTemporalColumns(cols)

  return cols
}

export async function addItemTableCharColumns(cols: Array<TableColumn>) {
  const db = await getDB()
  const attr = await db.attribute_char.find().exec()
  const { t } = i18n.global

  for (const el of attr) {
    cols.push({
      name: el.id,
      id: el.id,
      switchable: true,
      sortable: true,
      label: el.name,
      emptyText: (row: ItemTableData) =>
        !row.itemClassData?.attributes_char_item.includes(el.id)
          ? t("sfxTable.empty_incompatible")
          : undefined,
      field: (row: ItemTableData) => row.attributeChar?.get(el.id)?.value || null,
    })
  }

  return cols
}

export async function addItemTableNumericColumns(cols: Array<TableColumn>) {
  const db = await getDB()
  const attr = await db.attribute_numeric.find().exec()
  const { t } = i18n.global

  for (const el of attr) {
    cols.push({
      name: el.id,
      switchable: true,
      sortable: true,
      label: el.name,
      emptyText: (row: ItemTableData) =>
        !row.itemClassData?.attributes_numeric_item.includes(el.id)
          ? t("sfxTable.empty_incompatible")
          : undefined,
      field: (row: ItemTableData) => row.attributeNumeric?.get(el.id)?.value || null,
    })
  }

  return cols
}

export async function addItemTableSelectColumns(cols: Array<TableColumn>) {
  const db = await getDB()
  const attr = await db.attribute_select.find().exec()
  const { t } = i18n.global

  for (const el of attr) {
    cols.push({
      name: el.id,
      switchable: true,
      sortable: true,
      label: el.name,
      emptyText: (row: ItemTableData) =>
        !row.itemClassData?.attributes_select_item.includes(el.id)
          ? t("sfxTable.empty_incompatible")
          : undefined,
      field: (row: ItemTableData) => {
        const selectAttr = row.attributeSelect?.get(el.id)
        return selectAttr ? attributeSelectToString(selectAttr.attribute_select_options) : null
      },
    })
  }

  return cols
}

export async function addItemTableTemporalColumns(cols: Array<TableColumn>) {
  const db = await getDB()
  const attr = await db.attribute_temporal.find().exec()
  const { t } = i18n.global

  for (const el of attr) {
    cols.push({
      name: el.id,
      switchable: true,
      sortable: true,
      label: el.name,
      emptyText: (row: ItemTableData) =>
        !row.itemClassData?.attributes_temporal_item.includes(el.id)
          ? t("sfxTable.empty_incompatible")
          : undefined,
      field: (row: ItemTableData) => {
        const temporalAttr = row.attributeTemporal?.get(el.id)
        return temporalAttr ? attributeTemporalToString(temporalAttr.date, temporalAttr.time) : null
      },
    })
  }

  return cols
}

//endregion
